import React, {useContext, useEffect, useState} from 'react'
import {useIntl} from 'react-intl'
import axios from 'axios'
import {TableContainer} from 'Components/layout'
import {Button, DataTable} from 'Components/readonly'
import {Field, FieldGroup, Form} from 'Components/form'
import {TextInput} from 'Components/input'
import ValidationFailedMessage from 'Components/form/validationFailedMessage'
import {Column} from "primereact/column";
import styled from "styled-components";
import {AuthContext} from "../Context";

const Header = styled.header`
  padding-top: 1rem;
  margin-bottom: 1.3rem;

  h2, h3 {
    padding: 0;
  }

  h3 {
    font-size: 0.9rem;
    padding: 0;
  }
`

const SearchContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: last baseline;
`

const SdsTitle = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;

  .latest-sds-indicator {
    display: none;
  }

  &&.active {
    .latest-sds-indicator {
      width: 10px;
      height: 10px;
      background-color: #3bb377;
      border-radius: 50%;
      display: block;
    }
  }
`

//const SdsRegisterDialog = ({ modalController, modalData, searchParams }) => {
const SdsRegisterDialog = ({ modalController, searchParams }) => {
  const [params] = useState(searchParams ?? modalController?.searchParams ?? {});
  //const [params, setParams] = useState(searchParams ?? modalController?.searchParams ?? {});
  //const [data, setData] = useState(modalData ?? modalController?.data ?? {})
  const [error, setError] = useState(false)
  const {userLanguage} = useContext(AuthContext)
  const [inpChemical, setInpChemical] = useState(params?.chemical || "")
  const [inpSupplier, setInpSupplier] = useState(params?.supplier || "")
  const [inpLang] = useState(searchParams?.lang || userLanguage)
  const [hasParams] = useState((Object.keys(params).length > 0))
  const [chemicalId] = useState(parseInt(params?.id) > 0 ? parseInt(params.id) : false)
  const [searchResult, setSearchResult] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const intl = useIntl()

  const buildSearchParams = () => {
    const params = new URLSearchParams({
      chemical: inpChemical,
      supplier: inpSupplier,
      lang: inpLang,
    });
    return params.toString();
  }

  /**
   * Will search crawled SDS for a valid supplier database
   *
   * @returns {Promise<void>}
   */
  const fetchSds = async (pathName) => {
    const queryParams = buildSearchParams();
    const path = (typeof pathName === "string") ? pathName : '/search?' + queryParams;

    setIsLoading(true);
    try {
      const response = await axios({
        method: 'get',
        url: '/rapi/admin/sds-registry' + path,
      });
      setSearchResult(response?.data?.data ?? response?.data ?? []);
      setError(false);
    } catch (error) {
      setError(error.response.data.message);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    if(hasParams) {
      if(chemicalId) {
        fetchSds('/' + chemicalId + '?lang=' + inpLang);
      } else {
        fetchSds();
      }

    }
  }, [hasParams, chemicalId]);

  /**
   * Select and add SDS to the registry
   *
   * @param event
   * @param row
   */
  const addSdsToRegistry = async (event, row) => {
    const [data, setData] = modalController.data;
    row.uri_compare = '/rapi/chemicals/sds/' + row.current_sds;

    try {
      const response = await axios.post('/rapi/admin/sds-registry/blob', {
        uri: row.uri
      }, {
        responseType: 'blob'
      });

      row.sds_blob = response.data;
      setData(row);

    } catch (error) {
      console.log(error, data);
    }
  }

  /**
   * Preview table column
   *
   * @param row
   * @returns {JSX.Element}
   * @constructor
   */
  const PreviewColumn = (row) => (
    row.hash_match ? (
      <Button
        primary
        style={{margin: 0}}
        icon="pi pi-check-circle"
      />
    ) : (
      <Button
        primary
        style={{margin: 0}}
        icon="pi pi-download"
        onClick={(event) => {
          event.preventDefault();
          window.open(row.uri, "_blank")
        }}
      />
    )
 );

  /**
   * Add SDS table column
   *
   * @param row
   * @returns {JSX.Element}
   * @constructor
   */
  const AddSdsColumn = (row) => (
    <>
      {(row.file_type &&
        <Button
          primary
          style={{margin: 0}}
          icon="pi pi-arrow-circle-right p-c p-button-icon-left"
          label={intl.formatMessage({id: 'common.choose'})}
          onClick={(event) => addSdsToRegistry(event, row)}
        />
      )}
    </>
  );

  /**
   * Search result component
   *
   * @param children
   * @returns {JSX.Element}
   * @constructor
   */
  const SearchResults = ({ children }) => (
    <TableContainer className="fix-ie-table-width flextable" style={{maxWidth: "920px", width: "100%"}}>
      <DataTable
        responsive={true}
        className="clickable-rows"
        value={searchResult}
        loading={isLoading}
        paginator={false}
      >
        <Column
          style={{minWidth: '160px'}}
          key="title"
          field="title"
          header={intl.formatMessage({id: 'chemicalsds.registry.dialog.page_title'})}
          sortable={true}
          body={(row) => (
            <SdsTitle className={row.status === 2 ? "active" : ""}>
              <span className={"latest-sds-indicator"}></span>
              <span title={row.title}>{row.title}</span>
            </SdsTitle>
          )}
        />
        <Column
          key="source"
          field="source"
          header={intl.formatMessage({id: 'chemicalsds.registry.dialog.source'})}
          body={(row) => (
            <span title={row.uri}>
              {row.source}
            </span>
            )}
          sortable={true}
        />
        <Column
          key="file_type"
          field="file_type"
          header={intl.formatMessage({id: 'chemicalsds.registry.dialog.file_type'})}
          sortable={true}
        />
        <Column
          key="language"
          field="language"
          header={intl.formatMessage({id: 'chemicalsds.registry.dialog.language'})}
          sortable={true}
        />
        <Column
          key="info"
          field="info_short"
          header={intl.formatMessage({id: 'chemicalsds.registry.dialog.info'})}
          body={(row) => (
            <span title={row.info_long}>
              {row.info_short}
            </span>
            )}
          sortable={false}
        />
        <Column
          key="sds_date"
          field="sds_date"
          header={intl.formatMessage({id: 'chemicalsds.registry.dialog.sds_date'})}
          sortable={true}
        />
        <Column
          key="preview"
          field="preview"
          header={""}
          body={PreviewColumn}
          sortable={false}
        />
        {/* skip this for now
        <Column
          key="addSds"
          field="addSds"
          header={""}
          body={AddSdsColumn}
          sortable={false}
        />
        */}
        {children}
      </DataTable>
    </TableContainer>
  );

  return (
    <>
      {error && (
        <ValidationFailedMessage message={error} />
      )}
      {searchResult[0] && searchResult[0]?.chemical_name && (
        <>
          <Header>
            <h2>{searchResult[0].chemical_name}</h2>
            <h3>{searchResult[0].supplier}</h3>
          </Header>
        </>
      )}
      {!hasParams && (
        <Form onSubmit={fetchSds}>
          <FieldGroup>
            <SearchContainer>
              <Field
                id="inpChemical"
                key="inpChemical"
                label={"Chemical"}
                Component={TextInput}
                required={true}
                onChange={(e) => setInpChemical(e.target.value)}
                value={inpChemical}
              />
              <Field
                id="inpSupplier"
                key="inpSupplier"
                label={"Supplier"}
                Component={TextInput}
                required={true}
                onChange={(e) => setInpSupplier(e.target.value)}
                value={inpSupplier}
              />
              <div>
                <Button
                  primary
                  label={intl.formatMessage({id: 'common.find'})}
                  onClick={fetchSds}
                />
              </div>
            </SearchContainer>
          </FieldGroup>
        </Form>
      )}

      {searchResult && (
        <SearchResults />
      )}
    </>
  );
};

export default SdsRegisterDialog;
